import { pickVideoMailMimeType, shouldUploadTake, toUploadContentType } from './localCapture';
function getRecorderConstructor() {
    return globalThis.MediaRecorder;
}
/**
 * Whether this take has to be captured by the server-side recorder.
 *
 * The native app carries react-native-webrtc for calls and react-native-video
 * for playback, neither of which records to a file, so it still asks the
 * server-side recorder to join and pays the ten seconds that costs. The
 * absence of a media recorder is the honest test for that, and it also covers
 * a browser too old to have one.
 *
 * @returns {boolean} True when the server-side recorder must be used.
 */
export function usesServerRecorder() {
    return !getRecorderConstructor();
}
/**
 * Starts recording the given camera and microphone stream.
 *
 * @param {IStartOptions} options - The stream and its duration limit.
 * @returns {IVideoMailCapture} A handle that ends the take.
 */
export function startVideoMailCapture({ stream, maxDurationSeconds, onAutoStop }) {
    const Recorder = getRecorderConstructor();
    if (!Recorder) {
        throw new Error('This browser cannot record video. Try Chrome, Edge, Firefox or Safari.');
    }
    const mimeType = pickVideoMailMimeType(type => Recorder.isTypeSupported(type));
    if (!mimeType) {
        throw new Error('This browser cannot record a format we can store. Try a different browser.');
    }
    const chunks = [];
    const recorder = new Recorder(stream, { mimeType });
    const startedAt = Date.now();
    let limitTimer;
    recorder.ondataavailable = event => {
        if (event.data?.size) {
            chunks.push(event.data);
        }
    };
    // A timeslice means a lost tab still leaves usable chunks behind, and it
    // keeps a long take from living in one growing buffer.
    recorder.start(1000);
    if (maxDurationSeconds && maxDurationSeconds > 0) {
        limitTimer = setTimeout(() => {
            if (recorder.state !== 'inactive') {
                recorder.stop();
                onAutoStop?.();
            }
        }, maxDurationSeconds * 1000);
    }
    return {
        stop() {
            return new Promise((resolve, reject) => {
                if (limitTimer) {
                    clearTimeout(limitTimer);
                }
                const finish = () => {
                    const BlobCtor = globalThis.Blob;
                    const blob = new BlobCtor(chunks, { type: recorder.mimeType || mimeType });
                    const decision = shouldUploadTake({ size: blob.size, mimeType: blob.type });
                    if (!decision.upload) {
                        reject(new Error(decision.reason));
                        return;
                    }
                    resolve({
                        blob,
                        contentType: toUploadContentType(blob.type),
                        durationSeconds: Math.max(0, Math.round((Date.now() - startedAt) / 1000))
                    });
                };
                recorder.onerror = () => reject(new Error('The recording stopped unexpectedly. Please record again.'));
                if (recorder.state === 'inactive') {
                    finish();
                    return;
                }
                recorder.onstop = finish;
                recorder.stop();
            });
        }
    };
}
